import { useStore } from '@/app/providers/store';
import type { ReferencePrompt, SourceAudio, TempUploadItem } from '@/app/types';
import { uploadFile } from '@/app/utils/client';
import { useRunTempUploadBatch } from './use-run-temp-upload-batch';

type TempAudioPrompt = SourceAudio & {
  _tempFile?: File;
};

export const useReferenceTempAudiosUpload = (
  referencePrompts: ReferencePrompt[],
  updateReferenceAndMentions: (id: string, updates: Partial<ReferencePrompt>) => void,
) => {
  const userInfo = useStore((state) => state.userInfo);

  useRunTempUploadBatch(
    userInfo,
    'Failed to upload temp reference audio',
    ({ processed, pending }) =>
      referencePrompts
        .filter(
          (ref): ref is Extract<ReferencePrompt, { type: 'audio' }> & { prompt: TempAudioPrompt } =>
            ref.type === 'audio' && ref.prompt !== null && !!ref.prompt._tempFile,
        )
        .map((ref): TempUploadItem | null => {
          const uploadKey = `reference-temp-audio:${ref.id}`;
          if (processed.current.has(uploadKey) || pending.current.has(uploadKey)) return null;

          return {
            uploadKey,
            run: async () => {
              const currentRef = referencePrompts.find(
                (candidate): candidate is Extract<ReferencePrompt, { type: 'audio' }> & { prompt: TempAudioPrompt } =>
                  candidate.id === ref.id && candidate.type === 'audio' && candidate.prompt !== null,
              );
              const currentPrompt = currentRef?.prompt;
              if (!currentPrompt?._tempFile) return undefined;

              updateReferenceAndMentions(ref.id, { status: 'uploading' });
              const uploadResult = await uploadFile({
                file: currentPrompt._tempFile,
                duration: currentPrompt.duration,
              });
              if (uploadResult.error || !uploadResult.uri) {
                throw new Error(String(uploadResult.error ?? 'missing upload uri'));
              }

              const { _tempFile, ...promptWithoutTemp } = currentPrompt;
              updateReferenceAndMentions(ref.id, {
                status: 'done',
                prompt: {
                  ...promptWithoutTemp,
                  id: uploadResult.id ?? promptWithoutTemp.id,
                  src: uploadResult.uri,
                  preview: promptWithoutTemp.preview || uploadResult.uri,
                },
              });
              return undefined;
            },
          };
        })
        .filter((item): item is TempUploadItem => item !== null),
    [referencePrompts, updateReferenceAndMentions, userInfo],
  );
};
